<script lang="ts">
export type NxChecklistItem = {
  id: string | number;
  text: string;
  done: boolean;
};

let idCounter = 0;
/** Default id for a new entry: unique within the page, never sent anywhere. */
function localId(): string {
  idCounter += 1;
  return `new-${Date.now().toString(36)}-${idCounter}`;
}
</script>

<script setup lang="ts" generic="T extends NxChecklistItem">
/**
 * A checklist: entries with a check box, a progress „x/y“, adding, deleting
 * and reordering. The `NxEditableList` pattern for entries that can be ticked
 * off.
 *
 * Keyboard (in an entry's text): Enter adds an entry below and moves there,
 * Backspace in an empty entry removes it and goes back up, Alt + ↑/↓ moves the
 * entry. Pointer users drag the grip handle.
 *
 * Two ways to use it:
 * - **Inline** (default): the text is edited right in the row.
 * - **Openable** (`openable`): entries carry more than a text — a number, a
 *   period, an assignee — shown through the `#content` slot. A click or Enter
 *   on the row emits `open` and „add“ emits `add`; editing happens in the
 *   caller's dialog. Ticking and reordering stay in the list.
 *
 * New inline entries come from `createItem`, or get a local id from `createId`
 * (default: a page-unique string); a caller that saves entries on the server
 * replaces it with the real one.
 */
import { computed, nextTick, ref } from 'vue';
import { AddIcon, DeleteIcon, ReOrderDotsVerticalIcon } from '@blpde/nx-icons';
import NxIconButton from '../IconButton/NxIconButton.vue';
import NxCheckbox from '../Inputs/NxCheckbox.vue';
import NxProgressMeter from '../ProgressMeter/NxProgressMeter.vue';

const props = withDefaults(
  defineProps<{
    addLabel?: string;
    placeholder?: string;
    deleteLabel?: string;
    /** Accessible name of the drag handle. */
    moveLabel?: string;
    /** Accessible name of an entry's check box, from its text. */
    checkLabel?: (text: string, index: number) => string;
    /** Show the progress line. */
    progress?: boolean;
    /** Only ticking stays possible: no editing, adding, deleting or reordering. */
    readonly?: boolean;
    disabled?: boolean;
    /** Rows open in the caller's dialog instead of being edited inline (see above). */
    openable?: boolean;
    createId?: () => string | number;
    /** Factory for a new inline entry; wins over `createId`. */
    createItem?: () => T;
  }>(),
  {
    addLabel: 'Eintrag hinzufügen',
    placeholder: 'Neuer Eintrag…',
    deleteLabel: 'Löschen',
    moveLabel: 'Verschieben',
    checkLabel: (text: string, index: number) => text || `Eintrag ${index + 1}`,
    progress: true,
    readonly: false,
    disabled: false,
    openable: false,
    createId: localId,
    createItem: undefined,
  }
);

const model = defineModel<T[]>({ required: true });

const emit = defineEmits<{
  /** `openable`: a row was clicked or Enter was pressed on it. */
  open: [item: T, index: number];
  /** `openable`: the add button was used. */
  add: [];
}>();

defineSlots<{
  /** Row content next to the check box; replaces the text (input). */
  content?(props: { item: T; index: number }): unknown;
  /** Extra actions next to the add button, e.g. „Aus Katalog übernehmen“. */
  actions?(): unknown;
}>();

const doneCount = computed(() => model.value.filter((item) => item.done).length);
const editable = computed(() => !props.readonly && !props.disabled);

const rootRef = ref<HTMLElement | null>(null);

function focusText(index: number) {
  void nextTick(() => {
    rootRef.value?.querySelectorAll<HTMLInputElement>('.nx-checklist__text')[index]?.focus();
  });
}

function patch(index: number, change: Partial<T>) {
  const next = model.value.slice();
  next[index] = { ...next[index], ...change };
  model.value = next;
}

function insertAt(index: number) {
  const next = model.value.slice();
  const item = props.createItem
    ? props.createItem()
    : ({ id: props.createId(), text: '', done: false } as T);
  next.splice(index, 0, item);
  model.value = next;
  focusText(index);
}

function onAdd() {
  if (props.openable) emit('add');
  else insertAt(model.value.length);
}

/** Row click in `openable` mode — but not when the click was on a control. */
function onRowClick(event: MouseEvent, item: T, index: number) {
  if (!props.openable) return;
  const target = event.target as Element | null;
  if (target?.closest('button, input, a, [role="checkbox"], .nx-checklist__grip')) return;
  emit('open', item, index);
}

function onRowKeydown(event: KeyboardEvent, item: T, index: number) {
  if (!props.openable || event.key !== 'Enter' || event.target !== event.currentTarget) return;
  event.preventDefault();
  emit('open', item, index);
}

function remove(index: number) {
  model.value = model.value.filter((_, i) => i !== index);
}

function move(from: number, to: number) {
  if (to < 0 || to >= model.value.length || from === to) return;
  const next = model.value.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  model.value = next;
}

function onTextKeydown(event: KeyboardEvent, index: number) {
  if (event.isComposing) return;
  const input = event.target as HTMLInputElement;
  if (event.key === 'Enter') {
    event.preventDefault();
    insertAt(index + 1);
  } else if (event.key === 'Backspace' && input.value === '' && model.value.length > 0) {
    event.preventDefault();
    remove(index);
    focusText(Math.max(0, index - 1));
  } else if (event.altKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
    event.preventDefault();
    const target = index + (event.key === 'ArrowUp' ? -1 : 1);
    if (target < 0 || target >= model.value.length) return;
    move(index, target);
    focusText(target);
  }
}

// ── Drag to reorder (grip handle) ─────────────────────────────────────────
const dragFrom = ref<number | null>(null);
const dragOver = ref<number | null>(null);

function onDragStart(event: DragEvent, index: number) {
  dragFrom.value = index;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
    const row = (event.currentTarget as HTMLElement).closest('.nx-checklist__row');
    if (row) event.dataTransfer.setDragImage(row, 16, 16);
  }
}

function onDragOver(event: DragEvent, index: number) {
  if (dragFrom.value === null) return;
  event.preventDefault();
  dragOver.value = index;
}

function onDrop(index: number) {
  const from = dragFrom.value;
  dragFrom.value = null;
  dragOver.value = null;
  if (from !== null) move(from, index);
}

function onDragEnd() {
  dragFrom.value = null;
  dragOver.value = null;
}

function dropSide(index: number): 'before' | 'after' | null {
  if (dragFrom.value === null || dragOver.value !== index || dragFrom.value === index) return null;
  return index < dragFrom.value ? 'before' : 'after';
}
</script>

<template>
  <div ref="rootRef" class="nx-checklist" :class="{ 'is-disabled': disabled }">
    <NxProgressMeter
      v-if="progress && model.length > 0"
      class="nx-checklist__progress"
      :actual="doneCount"
      :target="model.length"
      unit=""
      variant="ok"
    />
    <ul class="nx-checklist__list">
      <li
        v-for="(item, index) in model"
        :key="item.id"
        class="nx-checklist__row"
        :class="{
          'is-done': item.done,
          'is-openable': openable,
          'is-drag-source': dragFrom === index,
          'is-drop-before': dropSide(index) === 'before',
          'is-drop-after': dropSide(index) === 'after',
        }"
        :tabindex="openable ? 0 : undefined"
        @click="onRowClick($event, item, index)"
        @keydown="onRowKeydown($event, item, index)"
        @dragover="onDragOver($event, index)"
        @drop.prevent="onDrop(index)"
      >
        <span
          v-if="editable"
          class="nx-checklist__grip"
          draggable="true"
          :title="moveLabel"
          :aria-label="moveLabel"
          role="img"
          @dragstart="onDragStart($event, index)"
          @dragend="onDragEnd"
        >
          <ReOrderDotsVerticalIcon filled class="nxt-icon-sm" />
        </span>
        <NxCheckbox
          :model-value="item.done"
          :disabled="disabled"
          :aria-label="checkLabel(item.text, index)"
          @update:model-value="patch(index, { done: $event } as Partial<T>)"
        />
        <div v-if="$slots.content" class="nx-checklist__content">
          <slot name="content" :item="item" :index="index" />
        </div>
        <input
          v-else-if="editable && !openable"
          class="nx-checklist__text"
          :value="item.text"
          :placeholder="placeholder"
          :aria-label="checkLabel(item.text, index)"
          @input="patch(index, { text: ($event.target as HTMLInputElement).value } as Partial<T>)"
          @keydown="onTextKeydown($event, index)"
        />
        <span v-else class="nx-checklist__text is-static">{{ item.text }}</span>
        <NxIconButton
          v-if="editable"
          class="nx-checklist__delete"
          data-test="delete"
          danger
          height="xs"
          :title="deleteLabel"
          @click="remove(index)"
        >
          <DeleteIcon filled class="nxt-icon-md" />
        </NxIconButton>
      </li>
    </ul>
    <div v-if="editable || $slots.actions" class="nx-checklist__footer">
      <button
        v-if="editable"
        type="button"
        class="nx-checklist__add"
        data-test="add"
        @click="onAdd"
      >
        <AddIcon filled class="nxt-icon-md" />
        {{ addLabel }}
      </button>
      <slot name="actions" />
    </div>
  </div>
</template>

<style scoped>
.nx-checklist {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
}
.nx-checklist__progress {
  margin-bottom: var(--nxt-space-2xs);
}
.nx-checklist__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-hairline);
  /* The global type reset sizes every <ul> at 16px medium. */
  font-size: inherit;
  font-weight: inherit;
}
.nx-checklist__row {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-height: var(--nxt-control-size-md);
  padding-inline: var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-md);
  transition: background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-checklist__row:hover {
  background: var(--nxt-surface-sunken-bg);
}
/* Delete stays quiet until the row is in play, then turns danger on hover. */
.nx-checklist__row .nx-checklist__delete:not(:hover, :focus-visible) {
  color: var(--nxt-fg-subtle);
}
.nx-checklist__delete {
  opacity: 0;
  transition: opacity var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-checklist__row:hover .nx-checklist__delete,
.nx-checklist__row:focus-within .nx-checklist__delete {
  opacity: 1;
}
@media (hover: none) {
  .nx-checklist__delete {
    opacity: 1;
  }
}
.nx-checklist__row.is-drag-source {
  opacity: 0.4;
}
.nx-checklist__row.is-drop-before::before,
.nx-checklist__row.is-drop-after::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: calc(var(--nxt-hairline) * 2);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-bg);
  pointer-events: none;
}
.nx-checklist__row.is-drop-before::before {
  top: calc(var(--nxt-space-2xs) / -2 - var(--nxt-hairline));
}
.nx-checklist__row.is-drop-after::after {
  bottom: calc(var(--nxt-space-2xs) / -2 - var(--nxt-hairline));
}
.nx-checklist__grip {
  display: inline-flex;
  color: var(--nxt-fg-subtle);
  cursor: grab;
  opacity: 0;
  transition: opacity var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-checklist__row:hover .nx-checklist__grip,
.nx-checklist__row:focus-within .nx-checklist__grip {
  opacity: 1;
}
.nx-checklist__text {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  font: inherit;
  color: var(--nxt-fg);
  border-radius: var(--nxt-radius-sm);
}
.nx-checklist__text:focus {
  outline: none;
  background: var(--nxt-input-bg-focus);
  box-shadow: inset 0 0 0 var(--nxt-hairline) var(--nxt-border-strong);
}
.nx-checklist__row.is-done .nx-checklist__text {
  color: var(--nxt-fg-muted);
  text-decoration: line-through;
}
.nx-checklist__row.is-openable {
  cursor: pointer;
  outline: none;
}
.nx-checklist__row.is-openable:focus-visible {
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-checklist__content {
  flex: 1;
  min-width: 0;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
}
.nx-checklist__row.is-done .nx-checklist__content {
  color: var(--nxt-fg-muted);
}
.nx-checklist__footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs);
}
.nx-checklist__add {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-height: var(--nxt-control-size-sm);
  border: var(--nxt-hairline) dashed var(--nxt-border-strong);
  border-radius: var(--nxt-radius-md);
  background: transparent;
  padding: 0 var(--nxt-space-sm);
  color: var(--nxt-fg-muted);
  cursor: pointer;
  font: inherit;
  font-weight: var(--nxt-font-weight-medium);
  transition:
    color var(--nxt-motion-fast) var(--nxt-ease-out),
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-checklist__add:hover {
  color: var(--nxt-brand-subtle-fg);
  border-color: var(--nxt-brand-bg);
  background: var(--nxt-brand-subtle-bg);
}
.nx-checklist__add:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
}
@media (prefers-reduced-motion: reduce) {
  .nx-checklist__row,
  .nx-checklist__grip,
  .nx-checklist__delete,
  .nx-checklist__add {
    transition: none;
  }
}
</style>
